import { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Camera, X, Upload, Plus, Loader2, AlertCircle, Info } from 'lucide-react';
import api from '@/lib/api';
import EXAMPLE_IMAGES from './exampleImages';
import { trackUploadStarted, trackUpload } from '@/lib/analytics';
import { IMAGE_UPLOAD_ACCEPT } from '@/lib/uploadAccept';

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

export default function ImageUploader({ slots = [], onImagesChange, existingImages = {}, categorySlug = '' }) {
  const [images, setImages] = useState(existingImages);
  const [extras, setExtras] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [activeSlot, setActiveSlot] = useState(null);

  const notify = useCallback((newImages, newExtras) => {
    if (onImagesChange) onImagesChange({ slotImages: newImages, extraImages: newExtras });
  }, [onImagesChange]);

  const uploadFile = async (file, slotKey) => {
    if (file.size > MAX_FILE_SIZE) {
      setError(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum is 20MB.`);
      return null;
    }
    setError(null);
    setUploading(slotKey);
    trackUploadStarted(categorySlug, slotKey);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('slot_key', slotKey);
      const res = await api.post('/uploads/item-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      trackUpload(categorySlug, slotKey);
      return res.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
      return null;
    } finally {
      setUploading(null);
    }
  };

  const handleSlotUpload = async (e, slotKey) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await uploadFile(file, slotKey);
    if (result) {
      const newImages = { ...images, [slotKey]: result };
      setImages(newImages);
      notify(newImages, extras);
    }
  };

  const handleExtraUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const result = await uploadFile(file, 'extra');
      if (result) {
        setExtras(prev => {
          const updated = [...prev, result];
          notify(images, updated);
          return updated;
        });
      }
    }
  };

  const removeSlotImage = (slotKey) => {
    const newImages = { ...images };
    delete newImages[slotKey];
    setImages(newImages);
    notify(newImages, extras);
  };

  const removeExtra = (idx) => {
    setExtras(prev => {
      const updated = prev.filter((_, i) => i !== idx);
      notify(images, updated);
      return updated;
    });
  };

  return (
    <div className="space-y-4" data-testid="image-uploader">
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ background: 'rgba(90,31,42,0.08)', color: '#5A1F2A' }}>
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {/* Slot-based uploads */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {slots.map((slot) => {
          const img = images[slot.slot_key];
          const isUploading = uploading === slot.slot_key;
          const example = EXAMPLE_IMAGES[categorySlug]?.[slot.slot_key];
          return (
            <div key={slot.slot_key} className="relative">
              <label
                className={`block cursor-pointer rounded-xl border-2 border-dashed transition-colors duration-200 overflow-hidden ${
                  img ? 'border-transparent' : 'border-stone hover:border-muted-foreground'
                }`}
                data-testid={`slot-${slot.slot_key}`}
              >
                <input
                  type="file"
                  accept={IMAGE_UPLOAD_ACCEPT}
                  className="hidden"
                  onChange={(e) => handleSlotUpload(e, slot.slot_key)}
                  disabled={isUploading}
                />
                {img ? (
                  <div className="aspect-square bg-stone/10 relative group">
                    <img
                      src={img.url}
                      alt={slot.slot_label}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={(e) => { e.preventDefault(); removeSlotImage(slot.slot_key); }}
                      className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="aspect-square flex flex-col items-center justify-center gap-1 p-2 relative">
                    {isUploading ? (
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    ) : example ? (
                      <>
                        <img src={example.url} alt={`Example: ${slot.slot_label}`} className="w-full h-full object-cover absolute inset-0 opacity-20 rounded-lg" loading="lazy" decoding="async" />
                        <Camera className="h-5 w-5 text-muted-foreground relative z-10" />
                        <span className="text-[9px] text-muted-foreground text-center leading-tight relative z-10 px-1">{example.tip}</span>
                      </>
                    ) : (
                      <Camera className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                )}
              </label>
              <div className="mt-1 text-center">
                <span className="text-[11px] font-medium text-ink leading-tight block">{slot.slot_label}</span>
                {slot.is_required && <span className="text-[9px] text-muted-foreground">Required</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Extra images */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-ink">Extra Photos</span>
          <span className="text-[10px] text-muted-foreground">Unlimited</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {extras.map((img, idx) => (
            <div key={idx} className="relative h-20 w-20 rounded-lg overflow-hidden border border-stone/50 group">
              <img src={img.url} alt={`Extra ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" decoding="async" />
              <button
                onClick={() => removeExtra(idx)}
                className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          <label className="h-20 w-20 rounded-lg border-2 border-dashed border-stone hover:border-muted-foreground flex items-center justify-center cursor-pointer transition-colors duration-200">
            <input
              type="file"
              accept={IMAGE_UPLOAD_ACCEPT}
              multiple
              className="hidden"
              onChange={handleExtraUpload}
              disabled={!!uploading}
            />
            {uploading === 'extra' ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <Plus className="h-4 w-4 text-muted-foreground" />
            )}
          </label>
        </div>
      </div>
    </div>
  );
}
